interface ErrorLog {
  timestamp: Date;
  errorMessage: string;
  errorStack?: string;
  componentStack?: string;
  route: string;
  userAgent: string;
  sessionId?: string;
}

class ErrorLogger {
  private logs: ErrorLog[] = [];
  private maxLogs = 50;

  log(error: Error, errorInfo?: { componentStack?: string }): void {
    const errorLog: ErrorLog = {
      timestamp: new Date(),
      errorMessage: error.message,
      errorStack: error.stack,
      componentStack: errorInfo?.componentStack,
      route: window.location.pathname,
      userAgent: navigator.userAgent,
      sessionId: this.getSessionId(),
    };

    this.logs.unshift(errorLog);

    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    this.persistToStorage();
    this.logToConsole(errorLog);
  }

  private logToConsole(errorLog: ErrorLog): void {
    console.group('🚨 Error Caught by Error Boundary');
    console.error('Message:', errorLog.errorMessage);
    console.error('Route:', errorLog.route);
    console.error('Timestamp:', errorLog.timestamp.toISOString());
    if (errorLog.errorStack) {
      console.error('Stack:', errorLog.errorStack);
    }
    if (errorLog.componentStack) {
      console.error('Component Stack:', errorLog.componentStack);
    }
    console.groupEnd();
  }

  private persistToStorage(): void {
    try {
      const serializedLogs = this.logs.map(log => ({
        ...log,
        timestamp: log.timestamp.toISOString(),
      }));
      localStorage.setItem('apollo_error_logs', JSON.stringify(serializedLogs));
    } catch (error) {
      console.error('Failed to persist error logs:', error);
    }
  }

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('apollo_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('apollo_session_id', sessionId);
    }
    return sessionId;
  }

  getLogs(): ErrorLog[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
    try {
      localStorage.removeItem('apollo_error_logs');
    } catch (error) {
      console.error('Failed to clear error logs:', error);
    }
  }

  loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('apollo_error_logs');
      if (stored) {
        const parsed = JSON.parse(stored);
        this.logs = parsed.map((log: any) => ({
          ...log,
          timestamp: new Date(log.timestamp),
        }));
      }
    } catch (error) {
      console.error('Failed to load error logs from storage:', error);
    }
  }
}

export const errorLogger = new ErrorLogger();
errorLogger.loadFromStorage();
